import { useState } from 'react';
import { Badge, Button, Input, Modal, ModalFooter, Select } from '../../../components';
import {
  useDisconnectWhatsAppNumber,
  useRenameWhatsAppNumber,
  useSetDefaultWhatsAppNumber,
} from '../hooks/useSettings';
import type { WhatsAppNumber } from '../services/settingsService';

interface WhatsAppNumbersListProps {
  numbers: WhatsAppNumber[];
}

function numberName(n: WhatsAppNumber): string {
  return n.label || n.displayNumber || n.phoneNumberId;
}

/** The store's WhatsApp numbers, with rename / make default / disconnect.
 *  Shown only when the store has more than one number. */
export default function WhatsAppNumbersList({ numbers }: WhatsAppNumbersListProps) {
  const rename = useRenameWhatsAppNumber();
  const setDefault = useSetDefaultWhatsAppNumber();
  const disconnect = useDisconnectWhatsAppNumber();

  const [renaming, setRenaming] = useState<WhatsAppNumber | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [removing, setRemoving] = useState<WhatsAppNumber | null>(null);
  const [successorId, setSuccessorId] = useState('');

  const active = numbers.filter((n) => n.status === 'active');
  const successors = removing ? active.filter((n) => n.id !== removing.id) : [];

  function openRename(n: WhatsAppNumber) {
    setRenaming(n);
    setLabelDraft(n.label ?? '');
  }

  function openRemove(n: WhatsAppNumber) {
    setRemoving(n);
    setSuccessorId(active.find((o) => o.id !== n.id)?.id ?? '');
  }

  function submitRename(e: React.FormEvent) {
    e.preventDefault();
    if (!renaming?.id) return;
    rename.mutate(
      { id: renaming.id, label: labelDraft.trim() },
      { onSuccess: () => setRenaming(null) }
    );
  }

  function confirmRemove() {
    if (!removing?.id) return;
    disconnect.mutate(
      { id: removing.id, newDefaultId: removing.isDefault ? successorId : undefined },
      { onSuccess: () => setRemoving(null) }
    );
  }

  return (
    <>
      <ul className="divide-y divide-line rounded-card border border-line">
        {numbers.map((n) => (
          <li
            key={n.id ?? n.phoneNumberId}
            className="flex flex-wrap items-center justify-between gap-3 p-4"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink">{n.label || 'Unnamed number'}</span>
                {n.isDefault && (
                  <Badge variant="info" size="sm">
                    Default
                  </Badge>
                )}
                {n.status === 'disconnected' ? (
                  <Badge variant="default" size="sm">
                    Disconnected
                  </Badge>
                ) : !n.configured ? (
                  <Badge variant="warning" size="sm">
                    Credentials missing
                  </Badge>
                ) : null}
              </div>
              <p className="mt-0.5 text-sm text-ink-2">
                {n.displayNumber || '—'}
                <span className="ml-2 text-xs text-ink-3">ID {n.phoneNumberId}</span>
              </p>
            </div>
            {n.id && n.status === 'active' && (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => openRename(n)}>
                  Rename
                </Button>
                {!n.isDefault && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => n.id && setDefault.mutate(n.id)}
                    isLoading={setDefault.isPending && setDefault.variables === n.id}
                  >
                    Make default
                  </Button>
                )}
                {active.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => openRemove(n)}>
                    Disconnect
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-ink-3">
        Messages to every number arrive in the one inbox. Replies go out on the number the
        customer wrote to; staff alerts and new conversations use the default number.
      </p>

      <Modal isOpen={!!renaming} onClose={() => setRenaming(null)} title="Rename number" size="sm">
        <form onSubmit={submitRename}>
          <Input
            label="Label"
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            placeholder="e.g. Abuja"
            maxLength={100}
            helperText="Shown on each conversation in the inbox"
          />
          <ModalFooter>
            <Button type="button" variant="ghost" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={rename.isPending}>
              Save
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        title={removing ? `Disconnect ${numberName(removing)}?` : 'Disconnect number'}
        size="md"
      >
        <div className="space-y-4 text-sm text-ink-2">
          <p>
            Pulse will stop receiving and sending messages on this number. Its conversations
            stay in the inbox; a reply to one of them will go out on the default number. You can
            connect it again later.
          </p>
          {removing?.isDefault && (
            <Select
              label="New default number"
              value={successorId}
              onChange={setSuccessorId}
              options={successors.map((n) => ({ value: n.id ?? '', label: numberName(n) }))}
            />
          )}
        </div>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmRemove}
            isLoading={disconnect.isPending}
            disabled={!!removing?.isDefault && !successorId}
          >
            Disconnect
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
